import type {ValidationRegistrar} from 'modules/VendorRegister/types';

import {DownloadOutlined, FileImageOutlined, UploadOutlined} from '@ant-design/icons';
import {Button} from '@ds/components';
import {toast} from '@Toast';
import {downloadBlob, getErrorMessage, getErrorStatus} from 'modules/VendorRegister/utils';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  downloadPdfContract,
  sendKycOtpCode,
  sendSignatureInfo,
  uploadFile
} from 'store/slices/vendor-registration/thunk';
import {convertToPng} from 'utils/Helper';

import OtpModal from './components/OtpModal/OtpModal';

type UploadSignatureProps = {
  registerValidation: ValidationRegistrar;
  onCompleted: () => void;
};

const UploadSignature: React.FC<UploadSignatureProps> = ({registerValidation, onCompleted}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {registerStatus} = useAppSelector((state) => state.vendorRegistration);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploadedSignatureId, setUploadedSignatureId] = useState<string | number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpTtl, setOtpTtl] = useState(0);
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const sendOtp = useCallback(async () => {
    try {
      const response = await dispatch(sendKycOtpCode()).unwrap();

      if (!response.isSuccess) {
        toast(response.message || 'خطا در ارسال کد تایید');
        return;
      }

      toast('کد تایید ارسال شد');
      setOtpModalOpen(true);
      setOtpTtl(response.ttl ?? 0);
    } catch (error) {
      if (getErrorStatus(error) === 403) {
        navigate('/vendor-register-overview');
        return;
      }

      throw error;
    }
  }, [dispatch, navigate]);

  const submitSignature = useCallback(
    async (otpCode?: string) => {
      if (!uploadedSignatureId) {
        toast('لطفا تصویر امضای خود را بارگذاری کنید.');
        return false;
      }

      if (!otpCode) {
        setOtpModalOpen(true);
        return false;
      }

      try {
        const response = await dispatch(
          sendSignatureInfo({signatureImage: uploadedSignatureId, otpCode})
        ).unwrap();

        if (response.isSuccess) {
          setOtpModalOpen(false);
          onCompleted();
          return true;
        }

        toast(response.message || 'خطا');
      } catch (error) {
        if (getErrorStatus(error) === 403) {
          navigate('/vendor-register-overview');
        } else {
          toast(getErrorMessage(error, 'خطا'));
        }
      }

      return false;
    },
    [dispatch, navigate, onCompleted, uploadedSignatureId]
  );

  const validateStep = useCallback(() => submitSignature(), [submitSignature]);

  useEffect(() => {
    registerValidation(validateStep);
  }, [registerValidation, validateStep]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    setUploadedSignatureId(null);
    setFile(null);

    if (!selectedFile.type.startsWith('image/')) {
      toast('فقط فایل‌های تصویری مجاز هستند.');
      event.target.value = '';
      return;
    }

    let convertedFile: File;

    try {
      convertedFile = await convertToPng(selectedFile);
    } catch {
      toast('فرمت فایل پشتیبانی نمی شود.');
      event.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', convertedFile);
    setUploading(true);

    try {
      const response = await dispatch(uploadFile(formData)).unwrap();

      if (response.isSuccess) {
        setUploadedSignatureId(response.data.id);
        setFile(selectedFile);
      } else {
        toast(response.message || 'خطا');
      }
    } catch (error) {
      toast(getErrorMessage(error, 'خطا در بارگذاری فایل.'));
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDownloadContract = async () => {
    if (!registerStatus.trackingNumber) {
      return;
    }

    try {
      const blob = await dispatch(
        downloadPdfContract({trackingNumber: registerStatus.trackingNumber})
      ).unwrap();
      downloadBlob(blob, `contract-${registerStatus.trackingNumber}.pdf`);
    } catch {
      toast('خطا در دانلود قرارداد');
    }
  };

  return (
    <div className="flex flex-col gap-2 px-4">
      {registerStatus.trackingNumber ? (
        <Button
          variant="fill"
          color="secondary"
          onClick={handleDownloadContract}
          startIcon={<DownloadOutlined aria-hidden />}
          fullWidth
        >
          مشاهده قرارداد
        </Button>
      ) : null}

      <section className="rounded-xl border border-dashed border-orange/50 bg-orange-wash p-4">
        <div className="flex items-start gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-white text-title-lg text-orange shadow-sm">
            <FileImageOutlined aria-hidden />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h2 className="text-body-sm-emphasis text-neutral-fg">تصویر امضا</h2>
            <p className="text-body-xs text-gray-34">
              یک تصویر واضح از امضای خود روی زمینه روشن انتخاب کنید.
            </p>
          </div>
        </div>
        <Button
          fullWidth
          size="md"
          variant="outline"
          className="mt-4 bg-white!"
          onClick={() => fileInputRef.current?.click()}
          loading={uploading}
          startIcon={uploading ? undefined : <UploadOutlined aria-hidden />}
        >
          {uploading ? 'در حال بارگذاری...' : file ? 'تغییر تصویر امضا' : 'انتخاب تصویر امضا'}
        </Button>
      </section>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        disabled={uploading}
      />

      {file && previewUrl ? (
        <div className="flex items-center gap-1.5 rounded-md border border-gray-03 bg-gray-01 p-2">
          <img className="size-15 rounded object-cover" src={previewUrl} alt="تصویر امضا" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="truncate text-body-xs text-neutral-fg">{file.name}</span>
            <span className="text-body-xs text-gray-34">
              {(file.size / (1024 * 1024)).toFixed(2)} مگابایت
            </span>
          </div>
        </div>
      ) : null}

      <OtpModal
        open={otpModalOpen}
        onClose={() => setOtpModalOpen(false)}
        sendOtp={sendOtp}
        onSubmit={submitSignature}
        title="تاییدیه شماره تلفن همراه"
        label={`برای تایید احراز هویت، رمز یک‌بارمصرف ارسال شده به شمارهٔ ${
          registerStatus.mobile ?? ''
        } را در زیر وارد کنید.`}
        otpTtl={otpTtl}
        setOtpTtl={setOtpTtl}
      />
    </div>
  );
};

export default UploadSignature;
